import { Locale } from 'shared/i18n/interface'
import { AnyAction } from 'redux'
import { getLogger } from 'shared/logger'
import { ContentService, ContentQuery } from 'shared/services/ContentService'
import { buildContentState, buildFacetState } from '~/app/store/buildContentState'
import { loadMoreCountSelector, currentPageInfoSelector } from '~/app/store/paging/selectors'
import { refinementIdsByFilterSelector } from '~/app/store/refinement/selectors'
import { categoryByIdSelector } from '~/app/store/filterCategories'
import { updateUrl } from '~/app/store/locationState/utils'
import { StringDictionary } from 'shared/types/util'
import {
  getGalleryNameSelector,
  getLocaleSelector,
  getMpvid,
  getQuantitySelector,
  selectedOptionsSelector,
  getDpcId,
  getDesignConceptId,
  getProductVersion,
} from '~/app/store/config'
import {
  getBypassApproval,
  getNoCache,
  getUseConstraints,
  getForcedRankingStrategyOverride,
  getIsDirectQuickView,
  getTemplatePurposes,
  getRawSets,
} from '~/app/store/debug'
import { CONTENT_REVERT, CONTENT_UPDATE } from '~/app/store/constants'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { getRawExperiments } from '~/app/store/experimentation'
import { v4 } from 'uuid'
import { getRenderPropsOverrides, getSearchBackend, getASPExperimentFlags, getDebugModeEnabled, getQuickViewId } from '~/app/store/debug/reducer'
import { booleanRenderPropertySelector, getCategoryFilters, getGalleryType, getProductKey } from '~/app/store/config/reducer'
import { isNotFalse } from '~/shared/heplers'
import { assembleASPExperimentFlags } from 'shared/experiments/utils/assembleASPExperimentFlags'
import { getMetadataQueryParams } from '~/app/store/content/utils'
import { INITIAL_STATE as DEFAULT_PAGE_STATE } from '~/app/store/paging/constants'
import { designPersonalizationContextIdSelector, hasImageSelector, customColorVariationsSelector } from '~/app/store/designPersonalization/selectors'
import { effectiveDesignCreationTypesSelector } from '~/app/store/designCreationTypes/selectors'
import { DesignPersonalizationContextService } from 'shared/services/DesignPersonalizationContextService'
import { PREVIOUSLY_ORDERED_DAYS_OF_HISTORY, TEMPLATE_INTERACTION_SUBTYPE, TEMPLATE_INTERACTION_TYPE } from '~/app/constants'
import {
  IMAGE_PLACEHOLDER_BOOST_KEY,
  IMAGE_PLACEHOLDER_BOOST_WEIGHT,
  PersonalizationBoostingExperimentFlags,
} from '~/app/features/Gallery/components/DesignPersonalization/constants'
import { taxonomyIdByCategoryIdSelector } from '~/app/store/filterCategories/reducer'
import { authDataSelector } from '~/app/store/auth/reducer'
import { previouslyOrderedDesignConceptIdSelector, setPreviouslyOrderedData } from '~/app/store/previouslyOrdered'
import { combineWithPrioritySets } from '~/shared/sets/combineWithPrioritySets'
import { IS_BROWSER } from '~/app/constants'
import { createPreviouslyOrderedSet } from '~/shared/sets/previouslyOrderedTemplate'
import { ThunkDispatch } from '~/app/store/interface'
import { AuthInfo } from 'server/utils/types'
import { createDesignPersonalizationSet } from '~/shared/sets/designPersonalizationTemplate'
import { addDebugApiCall } from '~/app/store/debugToolbar/actions'
import { PageNumberNotFoundError } from '~/app/store/content/errors'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryModelsUrl } from 'shared/types/gallery/models'
import type { State } from 'shared/types/gallery/state'
import { RenderProperty } from 'shared/renderProperties'
import type { DesignPersonalization } from 'shared/types/vp/designpersonalization'

/**
 * Gets personalized set from query params in state
 * @param state
 * @returns GalleryModelsUrl.JSONObject | undefined - The personalized set configuration
 */
const getDesignPersonalizationSet = (state: State.GlobalState): GalleryModelsUrl.JSONObject | undefined => {
  const dpcId = getDpcId(state)
  const designConceptId = getDesignConceptId(state)

  if (!dpcId || !designConceptId) {
    return undefined
  }

  return createDesignPersonalizationSet(designConceptId, dpcId)
}

/**
 * Redux action to get Previously Ordered set configuration
 * @param resolvedCanonicalId - The canonical ID from auth
 * @param resolvedAuthorizationHeader - The access token from auth
 * @returns Promise<string | null> - The designConceptId of the previously ordered template, or null if none
 */
export const getPreviouslyOrderedDesignConceptId =
  (resolvedCanonicalId: string, resolvedAuthorizationHeader: string) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<string | null> => {
    const state = getState()

    try {
      const templateInteractions = await DesignPersonalizationContextService.getTemplateInteractions(
        resolvedCanonicalId,
        resolvedAuthorizationHeader,
        [TEMPLATE_INTERACTION_TYPE.TEMPLATE_ORDERED],
        [TEMPLATE_INTERACTION_SUBTYPE.TEMPLATE_TOKEN],
        PREVIOUSLY_ORDERED_DAYS_OF_HISTORY,
      )

      if (!templateInteractions.length) {
        return null
      }

      const latestTemplate = templateInteractions.at(-1)

      if (!latestTemplate) {
        return null
      }

      const productKey = getProductKey(state)
      const productVersion = getProductVersion(state)
      const locale = getLocaleSelector(state)

      const matchingQuery: GalleryContentQuery.GetMatchingTemplatesQuery = {
        productKey,
        productVersion,
        culture: locale,
        templateIdentifier: decodeURIComponent(latestTemplate.data),
      }

      const matchingTemplates = await ContentService.getMatchingTemplatesForProduct(matchingQuery)

      if (!matchingTemplates.length) {
        return null
      }

      const { designConceptId } = matchingTemplates[0]
      dispatch(setPreviouslyOrderedData({ designConceptId, timestamp: latestTemplate.timestamp }))
      return designConceptId
    } catch (error) {
      getLogger().error({
        message: 'Failed to get previously ordered template',
        error: error as Error,
        canonicalId: resolvedCanonicalId,
      })
      return null
    }
  }

/**
 * Redux action to get all sets for the content query including previously ordered sets
 * @param resolvedCanonicalId - The canonical ID from auth
 * @param resolvedAuthorizationHeader - The access token from auth
 * @returns Promise<GalleryModelsUrl.JSONObject[]> - Combined sets array
 */
const getSets =
  (isInitialCall: boolean, resolvedCanonicalId: string | undefined, resolvedAuthorizationHeader: string | undefined) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<GalleryModelsUrl.JSONObject[]> => {
    const state = getState()
    const sets = getRawSets(state) || []
    let previouslyOrderedSet: GalleryModelsUrl.JSONObject | undefined

    // Get personalized set from query params in state
    const personalizedSet = getDesignPersonalizationSet(state)
    const dpcId = designPersonalizationContextIdSelector(state)

    const cachedId = previouslyOrderedDesignConceptIdSelector(state)
    const designConceptId =
      isInitialCall && resolvedAuthorizationHeader && resolvedCanonicalId
        ? await dispatch(getPreviouslyOrderedDesignConceptId(resolvedCanonicalId, resolvedAuthorizationHeader))
        : cachedId

    if (designConceptId) {
      previouslyOrderedSet = createPreviouslyOrderedSet(designConceptId, dpcId)
    }

    return combineWithPrioritySets(sets, previouslyOrderedSet, personalizedSet)
  }

type ContentCategories = {
  categories: string[][]
  facetCategories: string[]
}

type GetContentCategoriesFn = (selectedCategories: StringDictionary<string[]>) => ContentCategories

/**
 * Gets categories to filter and facet on
 *
 * Prune L1s out of request when filtering on L2s to get the correct results back
 *   ex: (L1,L2) in the QS would behave like (L1 OR L2) and return all L1 content plus L2, which isn't correct
 * @param state
 */
export function getContentCategories(state: State.GlobalState): GetContentCategoriesFn {
  const getCategory = categoryByIdSelector(state)

  return (selectedCategories: StringDictionary<string[]>): ContentCategories => {
    const parentIds = {} as StringDictionary<string>

    const roots = Object.values(state.filters.byId)
      .filter((f) => f.dimension === REFINEMENT_DIMENSION.CATEGORY)
      .map((f) => (f as State.CategoryFilter).categoryId.toString())

    // iterate over the categories for that filter,
    //   finding the max depth and then filtering on that depth
    // exclude L2s from facetCategories since we dont show L3s
    const categories = Object.values(selectedCategories).map((catIds) => {
      let maxDepth = 0

      catIds.forEach((catId) => {
        const category = getCategory(catId)

        if (!category) {
          throw new Error(`Invalid category. Category ${catId} does not exist`)
        }

        const { level } = category

        if (level > maxDepth) {
          maxDepth = level
        }

        // L3s will never be shown, so no facets needed
        if (level < 2) {
          parentIds[catId] = catId
        }
      })

      return catIds.filter((catId) => getCategory(catId).level === maxDepth)
    })

    return {
      categories,
      facetCategories: roots.concat(Object.keys(parentIds)),
    }
  }
}

interface ContentUpdateParams {
  newContent: GalleryContentQuery.Response
  locale: Locale
  query: ContentQuery
  quantity: GalleryModelsUrl.ValidParsedQsValue<number>
  qvContent?: GalleryContentQuery.TileEntityResponse
  oldState?: State.GlobalState
  getTaxonomyIdByCategoryId: (categoryId: string) => string
  selectedTaxonomyIds: string[]
}

function generateContentUpdate({
  newContent,
  locale,
  query,
  quantity,
  qvContent,
  oldState,
  getTaxonomyIdByCategoryId,
  selectedTaxonomyIds,
}: ContentUpdateParams): AnyAction {
  const loadMoreCount = oldState ? oldState.paging.loadMoreCount + 1 : DEFAULT_PAGE_STATE.loadMoreCount

  return {
    type: CONTENT_UPDATE,
    payload: {
      ...buildFacetState(newContent.results.facets),
      ...buildContentState({
        content: newContent.results.content,
        locale,
        quantity,
        getTaxonomyIdByCategoryId,
        qvContent,
        selectedTaxonomyIds,
        oldState,
      }),
      paging: {
        totalEntities: newContent.total,
        loadMoreCount,
        hasMoreTemplates: newContent.length + newContent.offset < newContent.total,
      },
      requestUrl: newContent.requestUrl,
      config: {
        viewId: v4(),
        lastLoadMoreLength: newContent.results.content.length,
      },
    },
    meta: {
      query,
    },
  }
}

function generateContentRevert(originalState: State.GlobalState, query: ContentQuery): AnyAction {
  return {
    type: CONTENT_REVERT,
    payload: {
      paging: originalState.paging,
      refinements: originalState.refinements,
    },
    meta: {
      query,
    },
  }
}

interface ContentUpdateOptions {
  actions?: AnyAction | AnyAction[]
  shouldUpdateUrl?: boolean
  loadMore?: boolean
  callback?: () => void
  latestOrderedTemplate?: DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteraction
  authInfo?: AuthInfo
  isInitialCall?: boolean
}

/**
 * Action Creator to update the content state.
 * Given a query to perform against the Content Service, retrieve the new
 * content and return the CONTENT_UPDATE action.
 */
export const contentUpdate =
  ({
    actions,
    authInfo: { canonicalId, authorizationHeader } = {},
    shouldUpdateUrl = true,
    loadMore = false,
    isInitialCall = false,
  }: ContentUpdateOptions = {}): GalleryContentQuery.Action =>
  async (dispatch, getState): Promise<AnyAction> => {
    const originalState = getState()
    const rawExperiments = getRawExperiments(originalState)
    const booleanRenderProperty = booleanRenderPropertySelector(originalState)
    const showPersonalizationUI = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
    const PersonalizationBoostingDisabled = booleanRenderProperty(RenderProperty.PersonalizationBoostingDisabled)

    if (actions) {
      ;[actions].flat().forEach(dispatch)
    }

    const state = getState()
    const { keywords: metadataKeywords, categories: metadataCategories, attributes: metadataAttributes } = getMetadataQueryParams(state)
    const refinements = refinementIdsByFilterSelector(state)
    const getTaxonomyIdByCategoryId = taxonomyIdByCategoryIdSelector(state)
    const { categories, facetCategories } = getContentCategories(state)({
      ...refinements.categories,
      ...(metadataCategories?.length && { meta: metadataCategories.map(String) }),
    })
    const quantity = getQuantitySelector(state)
    const designId = getQuickViewId(state)
    const isDirectQuickView = getIsDirectQuickView(state)
    const loadMoreCount = loadMoreCountSelector(state)

    const searchBackend = getSearchBackend(state)
    const debugEnabled = getDebugModeEnabled(state)
    const designCreationTypes = effectiveDesignCreationTypesSelector(state)
    const designPersonalizationContextId = designPersonalizationContextIdSelector(state)
    const customColorVariations = customColorVariationsSelector(state)
    const authData = authDataSelector(state)
    const debugMode = getDebugModeEnabled(state)
    const productVersion = getProductVersion(state)
    const galleryName = getGalleryNameSelector(state)

    const imagePersonalized = hasImageSelector(state)

    const boostedSearchableEnumeratedData: GalleryContentQuery.BoostedSearchableEnumeratedData[] = []
    let rolledOutExperimentFlags: GalleryModelsUrl.ASPExperimentFlags = {}

    // authorizationHeader and canonicalId are only passed during server-side rendering
    const resolvedauthorizationHeader = authorizationHeader ?? authData?.authorizationHeader
    const resolvedCanonicalId = canonicalId ?? authData?.canonicalId

    if (showPersonalizationUI && imagePersonalized && !PersonalizationBoostingDisabled) {
      rolledOutExperimentFlags = PersonalizationBoostingExperimentFlags
      boostedSearchableEnumeratedData.push({
        key: IMAGE_PLACEHOLDER_BOOST_KEY,
        value: 'true',
        weight: IMAGE_PLACEHOLDER_BOOST_WEIGHT,
      })
    }

    const experimentFlags = assembleASPExperimentFlags([], rawExperiments, getASPExperimentFlags(state), rolledOutExperimentFlags)

    const { page, pageSize, offset } = currentPageInfoSelector(state, loadMore ? loadMoreCount + 1 : DEFAULT_PAGE_STATE.loadMoreCount)

    const attributes = [...metadataAttributes, ...Object.values(refinements.attributes)]
    const facetTaxonomyIds = getCategoryFilters(state).map((i) => i.taxonomyId)

    const sets = await dispatch(getSets(isInitialCall, resolvedCanonicalId, resolvedauthorizationHeader))
    const query: ContentQuery = {
      galleryType: getGalleryType(state),
      productKey: getProductKey(state),
      categories,
      facetCategories,
      attributes,
      facetTaxonomyIds,
      bypassApproval: getBypassApproval(state),
      locale: getLocaleSelector(state),
      galleryName,
      productVersion,
      keyword: [...metadataKeywords, refinements.keyword].filter(isNotFalse),
      collection: refinements.collection,
      length: pageSize,
      mpvId: getMpvid(state),
      offset,
      selectedOptions: selectedOptionsSelector(state),
      noCache: getNoCache(state),
      useConstraints: getUseConstraints(state),
      forcedRankingStrategyOverride: getForcedRankingStrategyOverride(state),
      renderProps: getRenderPropsOverrides(state),
      templatePurposes: getTemplatePurposes(state),
      searchBackend,
      experimentFlags,
      designCreationTypes,
      designPersonalizationContextId,
      customColorVariations,
      debug: debugEnabled,
      boostedSearchableEnumeratedData,
      includeCategoryAndKeywordData: true,
      filterAltTextCompatibleTagging: true,
      sets,
    }

    try {
      const { data: newContent, meta, raw } = await ContentService.getContent(query)

      if (page > 1 && newContent.offset >= newContent.total) {
        throw new PageNumberNotFoundError(galleryName, page, `(offset [${newContent.offset}] >= total[${newContent.total}])`)
      }

      if (debugMode && !IS_BROWSER) {
        dispatch(addDebugApiCall({ meta, raw }))
      }

      let qvContent

      if (isDirectQuickView && designId) {
        const qvQuery: ContentQuery = (({
          galleryType,
          productKey,
          facetTaxonomyIds,
          bypassApproval,
          locale,
          galleryName,
          productVersion,
          mpvId,
          noCache,
          useConstraints,
          forcedRankingStrategyOverride,
          selectedOptions,
          renderProps,
          searchBackend,
          experimentFlags,
          debug,
          designCreationTypes,
          includeCategoryAndKeywordData,
          sets,
          designPersonalizationContextId,
          customColorVariations,
        }) => ({
          galleryType,
          productKey,
          facetTaxonomyIds,
          bypassApproval,
          locale,
          galleryName,
          productVersion,
          mpvId,
          noCache,
          useConstraints,
          forcedRankingStrategyOverride,
          selectedOptions,
          renderProps,
          searchBackend,
          experimentFlags,
          debug,
          designCreationTypes,
          includeCategoryAndKeywordData,
          sets,
          designPersonalizationContextId,
          customColorVariations,
          designIds: [designId],
          length: 1,
          attributes: Object.values(refinements.attributes),
        }))(query)

        ;[qvContent] = (await ContentService.getContent(qvQuery)).data.results.content

        if (!qvContent) {
          const message = `Failed to open quickView for QSP: ${designId}`

          getLogger().warn({
            message,
            query,
          })
        }
      }

      let contentUpdateAction: AnyAction = generateContentUpdate({
        newContent,
        locale: state.config.locale,
        query,
        quantity,
        qvContent,
        oldState: loadMore ? state : undefined,
        getTaxonomyIdByCategoryId,
        selectedTaxonomyIds: categories.flat(),
      })

      if (shouldUpdateUrl) {
        contentUpdateAction = updateUrl(contentUpdateAction)
      }

      return dispatch(contentUpdateAction)
    } catch (e) {
      // revert on the client, otherwise throw
      if (IS_BROWSER) {
        getLogger().error(e as Error)
        return dispatch(generateContentRevert(originalState, query))
      }
      throw e
    }
  }
